/**
 * usePublicProfile — fetch a user's full public profile by id.
 *
 * Hits `GET /api/users/:id/public`, which returns the same fields the
 * /network page sees (location, createdAt, organizationId, trades, market
 * types, service areas, bio, privacy flags, etc). Used by any place that
 * opens ProfilePreviewModal with only a user id in hand (Messages thread,
 * Referral detail, Organization members).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface PublicProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  companyName: string | null;
  location: string | null;
  organizationId: string | null;
  createdAt: string | null;
  trigPrimaryTradeName: string | null;
  companyLogoUrl: string | null;
  profileImageUrl: string | null;
  trades: string[];
  marketTypes: string[];
  serviceAreas: any;
  personalBio: string | null;
  companyBio: string | null;
  trigUserEmail: string | null;
  trigUserPhone: string | null;
  subscriptionPlan: string | null;
  showEmailToConnections: boolean | null;
  showPhoneToConnections: boolean | null;
}

export function usePublicProfile(userId: string | null | undefined, enabled: boolean = true) {
  return useQuery<PublicProfile>({
    queryKey: ['user-public-profile', userId],
    queryFn: async () => {
      const response: any = await api.get(`/api/users/${userId}/public`);
      return response.data;
    },
    enabled: !!userId && enabled,
    staleTime: 30_000,
  });
}

/**
 * Reshapes a PublicProfile into the partner shape ProfilePreviewModal expects.
 * - Maps companyName → company, personalBio → bio
 * - Stamps `status: 'connected'` so the modal treats the user as a connection
 *   (callers reach for this hook only for connected users — message thread
 *   partners, referral counterparties, org teammates).
 */
export function toProfilePreviewPartner(profile: PublicProfile) {
  return {
    ...profile,
    company: profile.companyName,
    bio: profile.personalBio,
    status: 'connected' as const,
  };
}
